import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays, parseISO } from 'date-fns';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Printer, Download, LayoutGrid, Bookmark, Link2Off, Timer } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { isoDate } from '../../utils/time';

const fmtCurrency = v => `$${parseFloat(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate     = d => { try { return format(parseISO(d + 'T00:00:00'), 'd MMM yyyy'); } catch { return d; } };
const fmtChartDate = d => { try { return format(parseISO(d + 'T00:00:00'), 'd MMM yyyy'); } catch { return d; } };

function StatCard({ bgColor, icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex overflow-hidden">
      <div className="w-20 shrink-0 flex items-center justify-center" style={{ background: bgColor }}>
        <Icon size={30} color="white" strokeWidth={1.8}/>
      </div>
      <div className="px-5 py-4 flex-1 min-w-0">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">{label}</p>
        <p className="text-2xl font-bold text-slate-800 truncate">{value}</p>
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg px-3 py-2.5 text-xs">
      <p className="font-semibold text-slate-700 mb-1.5">{label}</p>
      {payload.map(p => (
        <div key={p.dataKey} className="flex items-center gap-1.5 mb-0.5">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }}/>
          <span className="text-slate-600 capitalize">{p.dataKey}($):</span>
          <span className="font-semibold text-slate-800">{fmtCurrency(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

const STATUS_COLORS = {
  booked:    '#22c55e',
  completed: '#22c55e',
  published: '#22c55e',
  pending:   '#f59e0b',
  cancelled: '#ef4444',
  invoiced:  '#3b82f6',
};

export default function Performance() {
  const today = new Date();
  const [startDate, setStart] = useState(format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd'));
  const [endDate,   setEnd]   = useState(format(today, 'yyyy-MM-dd'));
  const [status,    setStatus]  = useState('');
  const [data,      setData]    = useState(null);
  const [loading,   setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (status) p.set('status', status);
    api.get(`/reports/performance?${p}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load performance report'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, status]);

  useEffect(() => { load(); }, [load]);

  function applyPreset(preset) {
    const now = new Date(); const y = now.getFullYear(); const m = now.getMonth();
    const iso = isoDate;
    if (preset === 'this-month')   { setStart(iso(new Date(y, m, 1)));     setEnd(iso(now)); }
    else if (preset === 'last-month')   { setStart(iso(new Date(y, m-1, 1))); setEnd(iso(new Date(y, m, 0))); }
    else if (preset === 'this-quarter') { const q = Math.floor(m/3)*3; setStart(iso(new Date(y, q, 1))); setEnd(iso(now)); }
    else if (preset === 'this-year')    { setStart(iso(new Date(y, 0, 1))); setEnd(iso(now)); }
    else if (preset === 'this-fy')      { const fyY = m >= 6 ? y : y-1; setStart(iso(new Date(fyY, 6, 1))); setEnd(iso(now)); }
  }

  function downloadCSV() {
    const rows = data?.shifts;
    if (!rows?.length) return;
    const cols = ['Date', 'Shift', 'Client', 'Area', 'Status'];
    const lines = [cols, ...rows.map(r => [r.date, r.shift, r.client, r.area, r.status])];
    const csv = lines.map(r => r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `performance_${startDate}_${endDate}.csv`;
    a.click();
  }

  const summary = data?.summary || {};
  const byDay = (data?.by_day || []).map(d => ({
    date:      fmtChartDate(d.date),
    invoiced:  parseFloat(d.invoiced  || 0),
    booked:    parseFloat(d.booked    || 0),
    pending:   parseFloat(d.pending   || 0),
    cancelled: parseFloat(d.cancelled || 0),
  }));
  const shifts = data?.shifts || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-slate-500 font-medium">Report</p>
          <h1 className="text-2xl font-bold text-slate-800">Performance</h1>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard bgColor="#0ea5e9" icon={LayoutGrid} label="Invoiced"  value={fmtCurrency(summary.invoiced)}  />
        <StatCard bgColor="#22c55e" icon={Bookmark}   label="Booked"    value={fmtCurrency(summary.booked)}    />
        <StatCard bgColor="#f59e0b" icon={Link2Off}   label="Cancelled" value={fmtCurrency(summary.cancelled)} />
        <StatCard bgColor="#ef4444" icon={Timer}      label="Pending"   value={fmtCurrency(summary.pending)}   />
      </div>

      {/* Chart + Table card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        {/* Controls */}
        <div className="flex justify-between items-center gap-3 px-5 py-3 border-b border-slate-200 flex-wrap">
          <select value={status} onChange={e => setStatus(e.target.value)}
            className="border border-slate-300 rounded px-3 py-1.5 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[160px]">
            <option value="">All Statuses</option>
            <option value="invoiced">Invoiced</option>
            <option value="booked">Booked</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {[['this-month','This Month'],['last-month','Last Month'],['this-quarter','Quarter'],['this-year','This Year'],['this-fy','This FY']].map(([key, label]) => (
              <button key={key} onClick={() => applyPreset(key)}
                className="px-2.5 py-1 text-xs font-medium rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 transition-colors">
                {label}
              </button>
            ))}
            <div className="flex items-center gap-1 border border-slate-300 rounded px-3 py-1.5 text-sm bg-white">
              <input type="date" value={startDate} onChange={e => setStart(e.target.value)}
                className="focus:outline-none bg-transparent text-slate-700 text-sm"/>
              <span className="text-slate-400 mx-1">-</span>
              <input type="date" value={endDate} onChange={e => setEnd(e.target.value)}
                className="focus:outline-none bg-transparent text-slate-700 text-sm"/>
            </div>
          </div>
        </div>

        {/* Area chart */}
        <div className="p-5">
          <p className="text-sm font-semibold text-slate-700 mb-3">Performance for</p>
          <div className="flex items-center gap-5 mb-4">
            {[['Invoiced($)','#3b82f6'],['Booked($)','#22c55e'],['Pending($)','#ef4444'],['Cancelled($)','#f59e0b']].map(([l,c]) => (
              <div key={l} className="flex items-center gap-1.5 text-xs text-slate-600">
                <span className="w-8 h-0.5 inline-block rounded" style={{ background: c }}/>
                {l}
              </div>
            ))}
          </div>

          {loading ? (
            <div className="h-56 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"/>
            </div>
          ) : byDay.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-slate-400 text-sm">
              No data for selected range
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={byDay} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                <defs>
                  {[['invoiced','#3b82f6'],['booked','#22c55e'],['pending','#ef4444'],['cancelled','#f59e0b']].map(([k,c]) => (
                    <linearGradient key={k} id={`gPerf_${k}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={c} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={c} stopOpacity={0.05}/>
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false}/>
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                  tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}/>
                <Tooltip content={<ChartTooltip/>}/>
                <Area type="monotone" dataKey="invoiced"  stroke="#3b82f6" fill="url(#gPerf_invoiced)"  strokeWidth={2} dot={{ r: 3, fill: '#3b82f6' }} activeDot={{ r: 4 }}/>
                <Area type="monotone" dataKey="booked"    stroke="#22c55e" fill="url(#gPerf_booked)"    strokeWidth={2} dot={{ r: 3, fill: '#22c55e' }} activeDot={{ r: 4 }}/>
                <Area type="monotone" dataKey="pending"   stroke="#ef4444" fill="url(#gPerf_pending)"   strokeWidth={2} dot={{ r: 3, fill: '#ef4444' }} activeDot={{ r: 4 }}/>
                <Area type="monotone" dataKey="cancelled" stroke="#f59e0b" fill="url(#gPerf_cancelled)" strokeWidth={2} dot={{ r: 3, fill: '#f59e0b' }} activeDot={{ r: 4 }}/>
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Shifts table */}
        <div className="border-t border-slate-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                {['Date','Staff Member','Client','Area','Status'].map(h => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-slate-600 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400 text-sm">Loading…</td></tr>
              ) : !shifts.length ? (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400 text-sm">No shifts found for selected range</td></tr>
              ) : shifts.map((row, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{fmtDate(row.date)}</td>
                  <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{row.shift}</td>
                  <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{row.client}</td>
                  <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{row.area}</td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    <span className="inline-flex px-2.5 py-0.5 rounded text-xs font-semibold text-white"
                      style={{ background: STATUS_COLORS[row.status] || '#94a3b8' }}>
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100">
          <button onClick={() => window.print()}
            className="w-9 h-9 rounded-xl border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50" title="Print">
            <Printer size={16}/>
          </button>
          <button onClick={downloadCSV}
            className="w-9 h-9 rounded-xl border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50" title="Download CSV">
            <Download size={16}/>
          </button>
        </div>
      </div>
    </div>
  );
}
